/**
 * Practitioner product access (HealthShopPlus / Turbo theme)
 *
 * Flow for a restricted product: logged out -> "Login to get access"; logged in -> the Powerful Form Builder
 * practitioner form; after the app reports a successful submission -> the theme's normal purchase controls.
 *
 * Completion detection: Powerful Form Builder dispatches `globo.formbuilder.form.submitted` on document only
 * when its server confirms the submission ("success": "true"), after recording it and its analytics. Failed
 * validation or failed requests never fire it. This script never submits or alters the app form.
 *
 * Completion is remembered per logged-in customer in this browser (localStorage). It is a convenience flag,
 * not authorization: logged-out visitors always get the login state from Liquid, and real enforcement
 * belongs in a Shopify Cart and Checkout Validation Function (custom app).
 */
(function () {
  'use strict';

  // The theme re-runs head scripts after InstantClick page changes; re-initialise instead of redefining.
  if (window.PractitionerAccess) {
    window.PractitionerAccess.init(document, true);
    return;
  }

  var COMPLETED_CLASS = 'pa-form-completed';
  var COMPLETED_KEY_PREFIX = 'practitionerFormCompleted:';
  var SUBMITTED_EVENT = 'globo.formbuilder.form.submitted';
  var CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var REMOVED_KEY = 'practitionerAccessRemoved';
  var FLAGS_CONCURRENCY = 6;

  var config = null;
  var listenersBound = false;
  var cartState = { itemCount: 0, items: [] };
  var cartRequest = null;
  var cartRequestQueued = false;
  var cartRemovalAttempted = false;
  var cartObserver = null;
  var cardObserver = null;
  var cardStates = {};
  var cardRequests = {};
  var cardScanQueued = false;

  /* ---------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------- */

  function readConfig() {
    var element = document.getElementById('PractitionerAccessConfig');
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  }

  function text(key) {
    return (config && config.text && config.text[key]) || '';
  }

  function storage(type) {
    try {
      return window[type];
    } catch (error) {
      return null;
    }
  }

  function storageGet(type, key) {
    var store = storage(type);
    try {
      return store ? store.getItem(key) : null;
    } catch (error) {
      return null;
    }
  }

  function storageSet(type, key, value) {
    var store = storage(type);
    try {
      if (store) store.setItem(key, value);
    } catch (error) {
      /* Storage unavailable: completion then lasts for this page view only. */
    }
  }

  function storageRemove(type, key) {
    var store = storage(type);
    try {
      if (store) store.removeItem(key);
    } catch (error) {
      /* ignore */
    }
  }

  function rootUrl() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root ? root : '/';
  }

  function productUrl(handle) {
    return rootUrl() + 'products/' + encodeURIComponent(handle);
  }

  // Only same-site paths are accepted for post-login redirects.
  function sameSitePath(value) {
    if (typeof value !== 'string' || value.charAt(0) !== '/' || /^\/[\/\\]/.test(value)) return null;
    try {
      var url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      return url.pathname + url.search + url.hash;
    } catch (error) {
      return null;
    }
  }

  /* ---------------------------------------------------------------------------
     Form completion (per logged-in customer, this browser)
     ------------------------------------------------------------------------- */

  var completion = {
    key: function () {
      return COMPLETED_KEY_PREFIX + config.customer.id;
    },
    isCompleted: function () {
      if (!config || !config.customer.loggedIn) return false;
      return document.documentElement.classList.contains(COMPLETED_CLASS) || storageGet('localStorage', completion.key()) === '1';
    },
    save: function () {
      storageSet('localStorage', completion.key(), '1');
    },
    reset: function () {
      if (config && config.customer.loggedIn) storageRemove('localStorage', completion.key());
      document.documentElement.classList.remove(COMPLETED_CLASS);
    },
    clearAll: function () {
      var store = storage('localStorage');
      if (!store) return;
      try {
        for (var i = store.length - 1; i >= 0; i--) {
          var key = store.key(i);
          if (key && key.indexOf(COMPLETED_KEY_PREFIX) === 0) store.removeItem(key);
        }
      } catch (error) {
        /* ignore */
      }
    }
  };

  // Logged-out visitors never inherit completion; logged-in customers get their own saved state.
  function syncCompletionClass() {
    var root = document.documentElement;
    if (!config.customer.loggedIn) {
      completion.clearAll();
      root.classList.remove(COMPLETED_CLASS);
      return;
    }
    root.classList.toggle(COMPLETED_CLASS, storageGet('localStorage', completion.key()) === '1');
  }

  function isLocked(state) {
    if (state === 'logged_out') return true;
    if (state === 'form_required') return !completion.isCompleted();
    return false;
  }

  /* ---------------------------------------------------------------------------
     Powerful Form Builder integration
     ------------------------------------------------------------------------- */

  function isPractitionerForm(detail) {
    if (!detail) return false;
    var form = detail.form;
    var className = config.form && config.form.className;
    if (form && form.closest && className) {
      var selector = '.' + (window.CSS && CSS.escape ? CSS.escape(className) : className);
      if (form.closest(selector)) return true;
    }
    var id = String((config.form && config.form.id) || '');
    var formId = detail.formId == null ? '' : String(detail.formId);
    if (!id || !formId) return false;
    var encoded = '';
    try {
      encoded = window.btoa(id);
    } catch (error) {
      /* ignore */
    }
    return formId === id || formId === encoded;
  }

  function onFormSubmitted(event) {
    if (!config || !config.customer.loggedIn || !isPractitionerForm(event.detail)) return;

    completion.save();

    var gates = document.querySelectorAll('[data-practitioner-access][data-state="form_required"][data-context="product"]');
    var success = config.success || {};
    if (success.show) {
      Array.prototype.forEach.call(gates, function (gate) {
        var panel = gate.querySelector('[data-practitioner-panel="success"]');
        var formPanel = gate.querySelector('[data-practitioner-panel="form"]');
        if (!panel) return;
        if (formPanel) formPanel.hidden = true;
        panel.hidden = false;
        gate.classList.add('practitioner-access--show-success');
        var heading = panel.querySelector('.practitioner-access__heading');
        if (heading) heading.focus();
      });
    }

    var delay = success.show ? Math.max(0, Math.min(5, Number(success.delay) || 0)) : 0;
    if (delay) window.setTimeout(unlock, delay * 1000);
    else unlock();
  }

  // Reveal the theme's own purchase controls (they are already in the page, hidden by CSS).
  function unlock() {
    document.documentElement.classList.add(COMPLETED_CLASS);
    Array.prototype.forEach.call(document.querySelectorAll('form[data-practitioner-check]'), applyCardState);
    applyCart();
  }

  // The app renders its placeholders once per page load. After a theme editor section reload or an
  // InstantClick page change, ask the app to render any placeholder that is still empty.
  function renderAppForms(scope) {
    var slots = scope.querySelectorAll('[data-practitioner-form-slot]:not([data-pa-render-requested])');
    var empty = Array.prototype.filter.call(slots, function (slot) {
      return !slot.querySelector('.globo-form');
    });
    if (!empty.length) return;
    var builder = window.Globo && window.Globo.FormBuilder;
    if (!builder || typeof builder.initialize !== 'function') return;
    empty.forEach(function (slot) {
      slot.setAttribute('data-pa-render-requested', '');
    });
    builder.initialize();
  }

  /* ---------------------------------------------------------------------------
     Product cards rendered in the browser (Boost filter / search)
     Server-rendered cards are handled in Liquid (snippets/product-thumbnail.liquid).
     ------------------------------------------------------------------------- */

  /*
    Card state uses the same server decision as the product page (snippets/practitioner-state.liquid) and the
    same completion flag (html.pa-form-completed). Whether a product is restricted is cached briefly in
    sessionStorage so browser-rendered cards can be decided instantly on later renders.
  */
  var RESTRICTED_CACHE_KEY = 'practitionerRestrictedProducts';
  var RESTRICTED_CACHE_TTL = 10 * 60 * 1000;
  var UNRESOLVED = 'unresolved';

  function readRestrictedCache() {
    try {
      var data = JSON.parse(storageGet('sessionStorage', RESTRICTED_CACHE_KEY) || '{}');
      return data && typeof data === 'object' ? data : {};
    } catch (error) {
      return {};
    }
  }

  function rememberCardState(handle, state) {
    cardStates[handle] = state;
    if (state === UNRESOLVED) return;
    var cache = readRestrictedCache();
    cache[handle] = { r: state !== 'normal' && state !== 'completed', t: Date.now() };
    storageSet('sessionStorage', RESTRICTED_CACHE_KEY, JSON.stringify(cache));
  }

  // Seed card states from the cache; the customer part of the state always comes from this page.
  function loadCachedCardStates() {
    var cache = readRestrictedCache();
    var now = Date.now();
    Object.keys(cache).forEach(function (handle) {
      var entry = cache[handle];
      if (!entry || now - entry.t > RESTRICTED_CACHE_TTL) return;
      if (cardStates[handle] !== undefined && cardStates[handle] !== UNRESOLVED) return;
      cardStates[handle] = entry.r ? (config.customer.loggedIn ? 'form_required' : 'logged_out') : 'normal';
    });
  }

  function cardActionElement(handle) {
    var cards = (config && config.cards) || {};
    var wrapper = document.createElement('div');
    wrapper.className = 'practitioner-card-action';
    wrapper.setAttribute('data-practitioner-card', '');
    if (cards.action === 'hide') return wrapper;

    var link = document.createElement('a');
    link.className = 'practitioner-card-action__button collection-quick-add__button ' + (cards.buttonClass || 'global-button global-button--primary');
    link.href = productUrl(handle) + (cards.action === 'get_access' ? '#practitioner-access' : '');
    var label = document.createElement('span');
    label.className = 'text';
    if (cards.action === 'get_access') {
      label.textContent = config.customer.loggedIn ? cards.accessText : (cards.loginText || cards.accessText);
    } else {
      label.textContent = cards.viewText;
    }
    link.appendChild(label);
    wrapper.appendChild(link);
    return wrapper;
  }

  // Sold out keeps the theme's own disabled button (no form), rather than an access link.
  function soldOutElement(button) {
    var wrapper = document.createElement('div');
    wrapper.className = 'practitioner-card-action';
    wrapper.setAttribute('data-practitioner-card', '');
    var clone = button.cloneNode(true);
    clone.type = 'button';
    clone.removeAttribute('name');
    clone.disabled = true;
    wrapper.appendChild(clone);
    return wrapper;
  }

  // Used by assets/boost-pfs-filter.js while building card HTML. null = render the normal quick add.
  function cardActionHtml(handle, available) {
    if (!config || !handle || available === false) return null;
    if (!Object.prototype.hasOwnProperty.call(cardStates, handle) || !isLocked(cardStates[handle])) return null;
    return config.cards && config.cards.action === 'hide' ? '' : cardActionElement(handle).outerHTML;
  }

  // Access could not be checked: never leave Add to cart; link to the product page, which decides itself.
  function unresolvedElement(handle) {
    var cards = (config && config.cards) || {};
    var wrapper = document.createElement('div');
    wrapper.className = 'practitioner-card-action';
    wrapper.setAttribute('data-practitioner-card', '');
    var link = document.createElement('a');
    link.className = 'practitioner-card-action__button collection-quick-add__button ' + (cards.buttonClass || 'global-button global-button--primary');
    link.href = productUrl(handle);
    var label = document.createElement('span');
    label.className = 'text';
    label.textContent = cards.viewText || 'View product';
    link.appendChild(label);
    wrapper.appendChild(link);
    return wrapper;
  }

  function applyCardState(form) {
    var handle = form.getAttribute('data-product-handle');
    var state = cardStates[handle];
    if (state === undefined || !form.parentNode) return;
    if (state === UNRESOLVED) {
      form.parentNode.replaceChild(unresolvedElement(handle), form);
      return;
    }
    if (isLocked(state)) {
      var button = form.querySelector('button[type="submit"], [name="add"]');
      var soldOut = button && (button.disabled || button.classList.contains('disabled'));
      form.parentNode.replaceChild(soldOut ? soldOutElement(button) : cardActionElement(handle), form);
    } else {
      form.removeAttribute('data-practitioner-check');
    }
  }

  // Parses a layout-none JSON template, tolerating HTML comments some stores/apps inject into responses.
  function parseJsonResponse(response) {
    if (!response.ok) throw new Error('request failed');
    return response.text().then(function (body) {
      var cleaned = body.replace(/<!--[\s\S]*?-->/g, '');
      var start = cleaned.indexOf('{');
      var end = cleaned.lastIndexOf('}');
      if (start === -1 || end < start) throw new Error('no JSON');
      return JSON.parse(cleaned.slice(start, end + 1));
    });
  }

  /*
    One lookup per product: /products/<handle>?view=practitioner-flag (templates/product.practitioner-flag.liquid).
    No "Accept: application/json" header: Shopify answers that with the product's raw JSON instead of the
    alternate template. Results are cached (see rememberCardState), so each product is checked once per session.
  */
  function fetchSingleCardState(handle) {
    return fetch(productUrl(handle) + '?view=practitioner-flag', { credentials: 'same-origin' })
      .then(parseJsonResponse)
      .then(function (data) {
        var state = data && data.products && data.products[handle];
        rememberCardState(handle, typeof state === 'string' ? state : UNRESOLVED);
      })
      .catch(function () {
        rememberCardState(handle, UNRESOLVED);
      });
  }

  function fetchCardStates(handles) {
    var queue = handles.slice();
    var requests = {};
    function worker() {
      var handle = queue.shift();
      if (!handle) return Promise.resolve();
      return requests[handle].run().then(worker);
    }
    // Every handle gets its own promise so a held click can wait for just that product.
    handles.forEach(function (handle) {
      var resolve;
      var done = new Promise(function (r) { resolve = r; });
      requests[handle] = { run: function () { return fetchSingleCardState(handle).then(resolve); } };
      cardRequests[handle] = done;
    });
    var workers = [];
    for (var i = 0; i < Math.min(FLAGS_CONCURRENCY, queue.length); i++) workers.push(worker());
    return Promise.all(workers);
  }

  function scanCards() {
    cardScanQueued = false;
    var unknown = [];
    Array.prototype.forEach.call(document.querySelectorAll('form[data-practitioner-check]'), function (form) {
      var handle = form.getAttribute('data-product-handle');
      if (!handle) return;
      if (cardStates[handle] !== undefined) applyCardState(form);
      else if (!cardRequests[handle] && unknown.indexOf(handle) === -1) unknown.push(handle);
    });
    if (!unknown.length) return Promise.resolve();
    return fetchCardStates(unknown).then(function () {
      Array.prototype.forEach.call(document.querySelectorAll('form[data-practitioner-check]'), applyCardState);
    });
  }

  function queueCardScan() {
    if (cardScanQueued) return;
    cardScanQueued = true;
    Promise.resolve().then(scanCards);
  }

  function observeCards() {
    if (cardObserver) cardObserver.disconnect();
    if (!('MutationObserver' in window) || !document.body) return;
    cardObserver = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var nodes = records[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (node.nodeType === 1 && (node.matches('form[data-practitioner-check]') || node.querySelector('form[data-practitioner-check]'))) {
            queueCardScan();
            return;
          }
        }
      }
    });
    cardObserver.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------------------
     Purchase guard: blocks theme cart forms for restricted products before the theme's own handlers run.
     ------------------------------------------------------------------------- */

  function isCartAddForm(form) {
    return /\/cart\/add/.test(form.getAttribute('action') || '');
  }

  function onSubmitCapture(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !config) return;

    var block = false;
    if (form.hasAttribute('data-practitioner-locked-form')) {
      block = true;
    } else if (form.closest('[data-practitioner-requires-form]') && isCartAddForm(form)) {
      block = !completion.isCompleted();
    } else if (form.hasAttribute('data-practitioner-check')) {
      var handle = form.getAttribute('data-product-handle');
      var state = cardStates[handle];
      if (state === undefined) {
        // Status not known yet: hold the submission until it is.
        block = true;
        var submitter = event.submitter || null;
        (cardRequests[handle] || fetchCardStates([handle])).then(function () {
          if (!document.contains(form)) return;
          if (cardStates[handle] === UNRESOLVED) {
            window.location.href = productUrl(handle);
            return;
          }
          applyCardState(form);
          if (!isLocked(cardStates[handle]) && document.contains(form) && typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter && form.contains(submitter) ? submitter : undefined);
          }
        });
      } else if (state === UNRESOLVED) {
        // Access could not be checked: let the product page decide instead of adding from the card.
        block = true;
        window.location.href = productUrl(handle);
      } else if (isLocked(state)) {
        block = true;
        applyCardState(form);
      }
    } else if (cartBlocked() && form.matches('[data-cart-form], #cart_form')) {
      block = !!(event.submitter && event.submitter.name === 'checkout');
    }

    if (block) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  /*
    Click guard (Eurus): the theme adds to cart and checks out from Alpine click handlers, not form submits.
    This capture-phase listener runs before those handlers and stops clicks on purchase controls that the
    customer may not use yet. Those controls are already hidden/omitted in Liquid; this is the safety net.
  */
  function checkoutBlocked(marker) {
    if (marker === 'locked') return true;
    if (marker === 'pending') return !completion.isCompleted();
    return false;
  }

  function onClickCapture(event) {
    if (!config || !(event.target instanceof Element)) return;
    var control = event.target.closest('button, a, input[type="submit"], [role="button"]');
    if (!control) return;

    var block = false;
    var checkout = control.closest('[data-practitioner-checkout]');
    if (checkout && checkoutBlocked(checkout.getAttribute('data-practitioner-checkout'))) {
      block = true;
    } else if (control.closest('[data-practitioner-locked]')) {
      block = true;
    } else if (control.closest('[data-practitioner-requires-form]') && !completion.isCompleted()) {
      block = true;
    }

    if (block) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  /* ---------------------------------------------------------------------------
     Cart handling (mini cart + cart page). Presentation only, not checkout enforcement.
     ------------------------------------------------------------------------- */

  function lockedCartLines() {
    return (cartState.items || []).filter(function (line) {
      return isLocked(line.state);
    });
  }

  function cartBlocked() {
    return !!config && config.cartBehaviour === 'block' && lockedCartLines().length > 0;
  }

  function clearCartDecorations() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-practitioner-cart-notice]'), function (notice) {
      notice.parentNode.removeChild(notice);
    });
    Array.prototype.forEach.call(document.querySelectorAll('.practitioner-cart-line'), function (line) {
      line.classList.remove('practitioner-cart-line');
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-practitioner-disabled]'), function (button) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('data-practitioner-disabled');
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-practitioner-hidden]'), function (element) {
      element.hidden = false;
      element.removeAttribute('data-practitioner-hidden');
    });
  }

  function cartNotice(message, url, modifier) {
    var notice = document.createElement('p');
    notice.className = 'practitioner-cart-notice' + (modifier ? ' practitioner-cart-notice--' + modifier : '');
    notice.setAttribute('data-practitioner-cart-notice', '');
    notice.appendChild(document.createTextNode(message));
    if (url && text('cartCta')) {
      notice.appendChild(document.createTextNode(' '));
      var link = document.createElement('a');
      link.href = url + '#practitioner-access';
      link.textContent = text('cartCta');
      notice.appendChild(link);
    }
    return notice;
  }

  function removedNotice() {
    // Separate marker so the cart re-decoration pass does not clear it.
    var notice = cartNotice(text('cartRemoved'), null, 'summary');
    notice.removeAttribute('data-practitioner-cart-notice');
    notice.setAttribute('role', 'status');
    return notice;
  }

  function showRemovedNotice() {
    if (storageGet('sessionStorage', REMOVED_KEY) !== '1') return;
    storageRemove('sessionStorage', REMOVED_KEY);
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-form], .js-empty-cart__message'), function (element) {
      element.insertBefore(removedNotice(), element.firstChild);
    });
    Array.prototype.forEach.call(document.querySelectorAll('.cart__continue-shopping'), function (link) {
      if (!link.closest('.js-empty-cart__message')) link.parentNode.insertBefore(removedNotice(), link);
    });
  }

  function removeLockedLines(lines) {
    if (cartRemovalAttempted) return;
    cartRemovalAttempted = true;
    var updates = {};
    lines.forEach(function (line) {
      updates[line.key] = 0;
    });
    fetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart update failed');
      storageSet('sessionStorage', REMOVED_KEY, '1');
      window.location.reload();
    }).catch(function () {
      // Could not remove: fall back to warnings with checkout blocked.
      decorateCart(lines, true);
    });
  }

  function decorateCart(lines, blockCheckout) {
    lines.forEach(function (line) {
      var selector = '[data-cart-item][data-line-id="' + line.line + '"][data-variant-id="' + line.variantId + '"]';
      Array.prototype.forEach.call(document.querySelectorAll(selector), function (item) {
        item.classList.add('practitioner-cart-line');
        var container = item.querySelector('.mini-cart__item-content, .cart__item--content') || item;
        container.appendChild(cartNotice(text('cartWarning'), line.url));
      });
    });

    if (!blockCheckout) return;

    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-form]'), function (form) {
      var checkoutButtons = form.querySelectorAll('[name="checkout"]');
      Array.prototype.forEach.call(checkoutButtons, function (button) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-practitioner-disabled', '');
      });
      if (checkoutButtons.length) {
        var anchor = checkoutButtons[0].closest('.checkout_button') || checkoutButtons[0];
        anchor.parentNode.insertBefore(cartNotice(text('cartWarning'), null, 'summary'), anchor);
      }
      Array.prototype.forEach.call(form.querySelectorAll('.additional-checkout-buttons'), function (element) {
        element.hidden = true;
        element.setAttribute('data-practitioner-hidden', '');
      });
    });
  }

  function applyCart() {
    if (!config) return;
    clearCartDecorations();

    var lines = lockedCartLines();
    if (lines.length) {
      if (config.cartBehaviour === 'remove') removeLockedLines(lines);
      else decorateCart(lines, config.cartBehaviour === 'block');
    }

    if (cartObserver) cartObserver.takeRecords();
  }

  function refreshCartStatus() {
    if (!config) return Promise.resolve();
    if (cartRequest) {
      cartRequestQueued = true;
      return cartRequest;
    }
    // No JSON Accept header: Shopify would answer /cart with the raw cart JSON instead of the template.
    cartRequest = fetch(config.routes.cartStatus, {
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(parseJsonResponse).then(function (data) {
      cartState = data && Array.isArray(data.items) ? data : { itemCount: 0, items: [] };
      applyCart();
    }).catch(function () {
      /* Keep the last known state. */
    }).then(function () {
      cartRequest = null;
      if (cartRequestQueued) {
        cartRequestQueued = false;
        refreshCartStatus();
      }
    });
    return cartRequest;
  }

  // The theme rebuilds the mini cart after Ajax updates; re-apply decorations when it does.
  function observeCart() {
    if (cartObserver) cartObserver.disconnect();
    if (!('MutationObserver' in window)) return;
    cartObserver = new MutationObserver(function () {
      applyCart();
    });
    Array.prototype.forEach.call(document.querySelectorAll('.js-cart_items'), function (list) {
      cartObserver.observe(list, { childList: true });
    });
  }

  // Detect cart changes made by the theme or apps (fetch or XHR) without wrapping either API.
  function watchCartRequests() {
    if ('PerformanceObserver' in window) {
      try {
        new PerformanceObserver(function (list) {
          var changed = list.getEntries().some(function (entry) {
            return (entry.initiatorType === 'fetch' || entry.initiatorType === 'xmlhttprequest') && CART_MUTATION_URL.test(entry.name);
          });
          if (changed) refreshCartStatus();
        }).observe({ type: 'resource', buffered: false });
        return;
      } catch (error) {
        /* fall through */
      }
    }
    if (window.jQuery) {
      window.jQuery(document).on('ajaxComplete', function (event, xhr, settings) {
        if (settings && CART_MUTATION_URL.test(settings.url || '')) refreshCartStatus();
      });
    }
  }

  /* ---------------------------------------------------------------------------
     Login return URL: keeps customers on the practitioner product after logging in.
     ------------------------------------------------------------------------- */

  function addHiddenInput(form, name, value) {
    if (form.querySelector('input[name="' + name + '"]')) return;
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  function initLoginReturn() {
    var target = sameSitePath(new URLSearchParams(window.location.search).get('return_url'));
    if (!target) return;

    Array.prototype.forEach.call(document.querySelectorAll('form'), function (form) {
      var type = form.querySelector('input[name="form_type"]');
      if (!type) return;
      if (type.value === 'customer_login') {
        addHiddenInput(form, 'checkout_url', target);
        addHiddenInput(form, 'return_url', target);
      } else if (type.value === 'create_customer') {
        addHiddenInput(form, 'return_to', target);
      }
    });

    var registerLink = document.getElementById('customer_register_link');
    if (registerLink && registerLink.href.indexOf('return_url=') === -1) {
      registerLink.href += (registerLink.href.indexOf('?') === -1 ? '?' : '&') + 'return_url=' + encodeURIComponent(target);
    }
  }

  /* ---------------------------------------------------------------------------
     Event wiring (bound once) and initialisation (safe to repeat).
     ------------------------------------------------------------------------- */

  function bindListeners() {
    if (listenersBound) return;
    listenersBound = true;

    window.addEventListener('submit', onSubmitCapture, true);
    window.addEventListener('click', onClickCapture, true);
    document.addEventListener(SUBMITTED_EVENT, onFormSubmitted);

    document.addEventListener('click', function (event) {
      var target = event.target instanceof Element ? event.target.closest('[data-practitioner-reset]') : null;
      if (!target) return;
      completion.reset();
      window.location.reload();
    });

    // Theme editor: re-initialise sections that are re-rendered.
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target, true);
    });

    watchCartRequests();

    if (window.InstantClick && typeof window.InstantClick.on === 'function') {
      window.InstantClick.on('change', function () {
        init(document, true);
      });
    }
  }

  function init(root, rerender) {
    config = readConfig();
    if (!config) return;

    syncCompletionClass();

    var scope = root && root.querySelectorAll ? root : document;
    if (scope === document) {
      cartState = config.cart || { itemCount: 0, items: [] };
      observeCart();
      loadCachedCardStates();
      observeCards();
      initLoginReturn();
      showRemovedNotice();
    }

    if (rerender) renderAppForms(scope);
    applyCart();
    queueCardScan();
  }

  var api = {
    version: '2.0.0',
    init: init,
    /** True when the logged-in customer has submitted the practitioner form in this browser. */
    isFormCompleted: function () {
      return completion.isCompleted();
    },
    /** Testing helper: forget this customer's form completion. */
    resetFormCompletion: function () {
      completion.reset();
    },
    refreshCart: refreshCartStatus,
    cardActionHtml: cardActionHtml
  };

  window.PractitionerAccess = api;

  function start() {
    bindListeners();
    init(document, false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
